import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  CircularProgress, 
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Chip,
  Link
} from '@mui/material';
import { MyLocation as MyLocationIcon, ExpandMore as ExpandMoreIcon, Help as HelpIcon } from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import { useTranslation } from 'react-i18next';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Capacitor } from '@capacitor/core';
import {
  getCurrentCoordinates,
  checkGeolocationPermission,
  GEOLOCATION_ERROR_CODES,
} from '../utils/geolocation';

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface MapRecenterProps {
  center: [number, number];
}

const MapRecenter: React.FC<MapRecenterProps> = ({ center }) => {
  const map = useMapEvents({});
  
  useEffect(() => {
    map.setView(center, map.getZoom());
  }, [center, map]);
  
  return null;
};

interface LocationMarkerProps {
  position: [number, number] | null;
  onPositionChange: (position: [number, number]) => void;
}

const LocationMarker: React.FC<LocationMarkerProps> = ({ position, onPositionChange }) => {
  useMapEvents({
    click(e) {
      const newPosition: [number, number] = [e.latlng.lat, e.latlng.lng];
      onPositionChange(newPosition);
    },
  });

  return position === null ? null : (
    <Marker position={position} />
  );
};

interface EmbeddedMapPickerProps {
  onLocationSelect: (lat: number, lng: number) => void;
  initialLocation?: { lat: number; lng: number } | null;
  height?: number;
}

export const EmbeddedMapPicker: React.FC<EmbeddedMapPickerProps> = ({
  onLocationSelect,
  initialLocation,
  height = 400,
}) => {
  const { t } = useTranslation();
  const [position, setPosition] = useState<[number, number] | null>(
    initialLocation ? [initialLocation.lat, initialLocation.lng] : null
  );
  const [geoLocationError, setGeoLocationError] = useState<string>('');
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState<'granted' | 'denied' | 'prompt' | 'unknown'>('unknown');
  const initialLoadRef = useRef(true);

  // Default to Dubai coordinates if no initial position
  const defaultCenter: [number, number] = [25.2048, 55.2708];
  const mapCenter = position || defaultCenter;

  // Check permission status on component mount
  useEffect(() => {
    checkGeolocationPermission().then(setPermissionStatus);

    if (!Capacitor.isNativePlatform() && navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: 'geolocation' })
        .then((permissionStatus) => {
          permissionStatus.addEventListener('change', () => {
            setPermissionStatus(permissionStatus.state);
          });
        })
        .catch(() => {});
    }
  }, []);

  // Wrap the position update to call the parent callback
  const updatePosition = useCallback((newPosition: [number, number]) => {
    setPosition(newPosition);
    if (!initialLoadRef.current) {
      onLocationSelect(newPosition[0], newPosition[1]);
    }
  }, [onLocationSelect]);

  // Mark initial load as complete after first render
  useEffect(() => {
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
    }
  }, []);

  // Update position when initialLocation changes (but don't override user selection)
  useEffect(() => {
    if (initialLocation && !position) {
      setPosition([initialLocation.lat, initialLocation.lng]);
    }
  }, [initialLocation, position]);

  const handleUseCurrentLocation = async () => {
    setGeoLocationError('');
    setIsGettingLocation(true);

    // Check if we're on HTTPS or localhost (not applicable on native, where
    // the app always runs under Capacitor's https:// scheme)
    const isSecureContext = Capacitor.isNativePlatform() ||
                          window.location.protocol === 'https:' ||
                          window.location.hostname === 'localhost' ||
                          window.location.hostname === '127.0.0.1';

    if (!isSecureContext) {
      setGeoLocationError(t('location_requires_https'));
      setIsGettingLocation(false);
      setShowHelp(true);
      return;
    }

    try {
      const { latitude, longitude } = await getCurrentCoordinates({
        enableHighAccuracy: true,
        timeout: 15000,
      });
      updatePosition([latitude, longitude]);
      setIsGettingLocation(false);
      setGeoLocationError('');
      setShowHelp(false);
      setPermissionStatus('granted');
    } catch (error: any) {
      console.error('Geolocation error:', error);
      setIsGettingLocation(false);

      if (error?.message === 'geolocation_not_supported') {
        setGeoLocationError(t('geolocation_not_supported'));
        setShowHelp(true);
        return;
      }

      switch (error?.code) {
        case GEOLOCATION_ERROR_CODES.PERMISSION_DENIED:
          setGeoLocationError(t('location_access_denied_detailed'));
          setShowHelp(true);
          setPermissionStatus('denied');
          break;
        case GEOLOCATION_ERROR_CODES.POSITION_UNAVAILABLE:
          setGeoLocationError(t('location_unavailable_detailed'));
          setShowHelp(true);
          break;
        case GEOLOCATION_ERROR_CODES.TIMEOUT:
          setGeoLocationError(t('location_timeout_detailed'));
          break;
        default:
          setGeoLocationError(t('location_error_detailed'));
          setShowHelp(true);
          break;
      }
    }
  };

  const getLocationButtonVariant = () => {
    if (permissionStatus === 'denied') return 'outlined';
    if (permissionStatus === 'granted') return 'contained';
    return 'outlined';
  };

  const getLocationButtonColor = () => {
    if (permissionStatus === 'denied') return 'warning';
    if (permissionStatus === 'granted') return 'primary';
    return 'primary';
  };

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('click_map_to_select_location')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            startIcon={isGettingLocation ? <CircularProgress size={16} /> : <MyLocationIcon />}
            variant={getLocationButtonVariant()}
            color={getLocationButtonColor()}
            size="small"
            onClick={handleUseCurrentLocation}
            disabled={isGettingLocation}
          >
            {isGettingLocation ? t('getting_location') : t('use_current_location')}
          </Button>
          <Button
            startIcon={<HelpIcon />}
            variant="text"
            size="small"
            onClick={() => setShowHelp(!showHelp)}
          >
            {t('help')}
          </Button>
        </Box>
      </Box>

      {permissionStatus === 'denied' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            {t('location_permission_denied_status')}
          </Typography>
        </Alert>
      )}

      {geoLocationError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {geoLocationError}
        </Alert>
      )}

      {showHelp && (
        <Accordion expanded={showHelp} sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">
              {t('location_access_help')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" gutterBottom>
              {t('location_access_help_intro')}
            </Typography>
            
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              {t('browser_instructions')}:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label="Chrome" size="small" />
                      <Typography variant="body2">
                        {t('chrome_location_instructions')}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label="Firefox" size="small" />
                      <Typography variant="body2">
                        {t('firefox_location_instructions')}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label="Safari" size="small" />
                      <Typography variant="body2">
                        {t('safari_location_instructions')}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            </List>
            
            <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
              {t('location_access_alternative')}: {t('click_map_to_select_manually')}
            </Typography>
          </AccordionDetails>
        </Accordion>
      )}

      <Box sx={{ height, width: '100%', border: '1px solid #ccc', borderRadius: 1 }}>
        <MapContainer
          center={mapCenter}
          zoom={13}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          <MapRecenter center={mapCenter} />
          <LocationMarker position={position} onPositionChange={updatePosition} />
        </MapContainer>
      </Box>
    </Box>
  );
};
